// client/src/hooks/useUploadPlayerStats.ts
//
// Hook para subir stats de torneos a player_stats en Supabase.
//
// Diseñado como fire-and-forget desde handleImport:
// - Se llama DESPUÉS de persistir las sessions localmente
// - NO bloquea la UI — el user ve el resultado del import inmediatamente
// - Errores se loggean pero no rompen el flow del import
// - Usa UPSERT con el unique constraint (user_id, tournament_id) para
//   que re-imports no dupliquen
//
// El upload solo sucede si el user opted-in. El caller es responsable
// de verificar el consent antes de llamar.

import { useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { ParsedTournament } from '../utils/pokerCraftParser';
import { buildPlayerStatsPayload, PlayerStatsRow } from '../utils/buildPlayerStatsPayload';

export interface UploadStatsInput {
  tournament: ParsedTournament;
  buyIn: number;
}

export interface UploadStatsResult {
  attempted: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export function useUploadPlayerStats(user: User | null) {
  /**
   * Sube en batch las stats de los torneos provistos.
   *
   * Implementación: UPSERT por batches para minimizar round-trips a Supabase.
   * Si un batch falla, se sigue con los siguientes — reporta totales al final.
   * Nunca throws; el caller ya persistió las sessions localmente y no queremos
   * romper el flow del import.
   */
  const uploadStats = useCallback(
    async (inputs: UploadStatsInput[]): Promise<UploadStatsResult> => {
      const result: UploadStatsResult = {
        attempted: inputs.length,
        succeeded: 0,
        failed: 0,
        skipped: 0,
      };

      if (!SUPABASE_ENABLED || !user || inputs.length === 0) {
        result.skipped = inputs.length;
        return result;
      }

      // Build payloads — skip tournaments that don't meet minimum criteria
      const rows: PlayerStatsRow[] = [];
      for (const { tournament, buyIn } of inputs) {
        const row = buildPlayerStatsPayload(user.id, tournament, buyIn);
        if (row) {
          rows.push(row);
        } else {
          result.skipped += 1;
        }
      }

      if (rows.length === 0) return result;

      // Upload in batches of 100 to minimize round-trips
      const BATCH_SIZE = 100;
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        try {
          const { error } = await supabase
            .from('player_stats')
            .upsert(batch, { onConflict: 'user_id,tournament_id' });

          if (error) {
            console.warn(
              `[useUploadPlayerStats] batch ${i / BATCH_SIZE + 1} failed:`,
              error.message
            );
            result.failed += batch.length;
          } else {
            result.succeeded += batch.length;
          }
        } catch (err: any) {
          console.warn(
            `[useUploadPlayerStats] batch ${i / BATCH_SIZE + 1} threw:`,
            err?.message || err
          );
          result.failed += batch.length;
        }
      }

      return result;
    },
    [user]
  );

  return { uploadStats };
}
